import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import IconButton from 'components/common/IconButton';
import { errorNotification, handleApiError, successNotification } from 'utils';
import { uploadCandidates } from 'api/files/files';

/**
 * @component UploadCandidates
 * @description
 * Componente que permite importar un archivo de candidatos en formato `.xls` o `.xlsx`.
 * Una vez cargado, el archivo se envía al backend y se notifica el resultado.
 *
 * @param {Object} props
 * @param {Function} props.onSubmitFile - Función callback que se ejecuta después de una carga exitosa.
 *
 * @returns {JSX.Element}
 */
function UploadCandidates({ onSubmitFile }) {
	const token = useSelector(state => state.authentication.user?.token);

	const [file, setFile] = useState(null);
	const fileInputRef = useRef(null);

	/**
	 * @function fetchUploadFiles
	 * @description
	 * Envía el archivo al backend mediante API y maneja la respuesta.
	 * @param {FormData} form - FormData con el archivo.
	 * @param {string} newFileName - Nombre del archivo que se cargará.
	 */
	const fetchUploadFiles = async (form, newFileName) => {
		try {
			const response = await uploadCandidates(form, token);
			if (response.data.status === 200) {
				successNotification("Archivo cargado correctamente.");
				onSubmitFile(response.data);
				handleFileReset();
			} else {
				errorNotification("No es posible subir el archivo.");
			}
		} catch (error) {
			handleApiError(error);
		}
	};

	/**
	 * @function handleFileChange
	 * @description
	 * Maneja el cambio de archivo en el input, validando el formato.
	 */
	const handleFileChange = (event) => {
		const selectedFile = event.target.files[0];
		if (selectedFile) {
			const fileName = selectedFile.name.toLowerCase();
			if (fileName.endsWith('.xls') || fileName.endsWith('.xlsx')) {
				setFile(selectedFile);
			} else {
				errorNotification('Formato de archivo no válido. Por favor, selecciona un archivo en formato xls.');
				setFile(null);
			}
		}
	};

	/**
	 * @function handleFileInputClick
	 * @description
	 * Simula el clic en el input de archivo oculto.
	 */
	const handleFileInputClick = () => {
		fileInputRef.current.click();
	};

	/**
	 * @function handleUpload
	 * @description
	 * Prepara el FormData y llama al método de carga si hay archivo seleccionado.
	 */
	const handleUpload = async () => {
		if (file) {
			const form = new FormData();
			const newFileName = `${file.name}`;
			form.append('uploaded_file', file, newFileName);
			fetchUploadFiles(form, newFileName);
		} else {
			errorNotification("Es necesario seleccionar un archivo.");
		}
	};

	/**
	 * @function handleFileReset
	 * @description
	 * Resetea el archivo seleccionado.
	 */
	const handleFileReset = () => {
		setFile(null);
		fileInputRef.current.value = '';
	};

	return (
		<>
			<input
				type="file"
				className="form-control"
				id="submitFile"
				onChange={handleFileChange}
				style={{ display: 'none' }}
				ref={fileInputRef}
			/>
			<IconButton
				variant="falcon-default"
				size="sm"
				icon={file ? "file-upload" : "file-import"}
				transform="shrink-3"
				className="me-2"
				iconAlign="middle"
				onClick={file ? handleUpload : handleFileInputClick}
			>
				<span className="d-none d-sm-inline-block d-xl-none d-xxl-inline-block ms-1">
					{file ? 'Subir Archivo' : 'Importar'}
				</span>
			</IconButton>
			{file ? (
				<>
					<span className="text-success me-2">{file.name}</span>
					<IconButton
						variant="falcon-default"
						size="sm"
						icon="times"
						transform="shrink-3"
						className="me-2"
						iconAlign="middle"
						onClick={handleFileReset}
					/>
				</>
			) : (
				<span className="text-muted"></span>
			)}
		</>
	);
}

export default UploadCandidates;